// Typed URL search params for the pipeline's three addresses (spec 09 §4.14,
// spec 16 §3.12, D-312, D-314; P3-E2 PR E2-1).
//
//   /sales/opportunities  the deal list, which adopts saved views as
//                         `sales.opportunities`: view, status, stage, owner,
//                         customer, close_from, close_to, rotting,
//                         needs_next_step, lost_reason, q, order, and peek
//   /sales/pipeline       the board: owner, customer, new_deal,
//                         from_conversation, and peek
//   /sales/forecast       the forecast: from_month, to_month, owner, customer
//
// The deal page (`/sales/opportunities/$opportunityId`) takes no parameter.
//
// THE LIST'S GRAMMAR IS THE CONTRACT'S. Its filter keys are the registry's
// (`SAVED_VIEW_SCREENS` in packages/contracts/src/saved-views/screens.ts,
// restated for the web as `SAVED_VIEW_SCREENS_WEB`), and its search type is
// pinned to the generated `SavedViewOpportunitiesConfig` filters in both
// directions (`FilterKeyPin`), so a filter the contract adds, drops or
// reshapes fails tsc here. A view stores `status` only as `won` or `lost`,
// because an absent status is `open`, and `rotting` and `needs_next_step` only
// as `true`. The address holds the same values and no others, so two equal
// views never compare unequal and a stored filter this validator drops is
// genuinely stale (spec 16 §3.12 item 7).
//
// THE BOARD CARRIES NO SAVED VIEW (D-312 ruling 20). Its two filters are the
// list's `owner` and `customer`, under the list's own keys, so the tab strip
// carries them from one page to the next. `new_deal=<customer uuid>` opens New
// deal with the customer locked, and `from_conversation=<uuid>` beside it
// records the thread the inbox's Create deal door came from (spec 09 §4.14,
// spec 17 §4.1). A conversation with no customer to lock names nothing the
// dialog can use, so it is dropped without `new_deal`. The screen removes both
// with a replace on save or cancel.
//
// `peek` IS SCREEN STATE OUTSIDE EVERY VIEW (D-314 ruling 8). The list and the
// board read it through the side peek's own codec
// (`components/peek-address.ts`), which accepts `<kind>:<uuid>` for the four
// kinds and writes it lower-cased. The saved-views layer keeps it through
// every write because it is not a filter key.
//
// DEFENSIVE, as every sales validator is (`search-params.ts` states the house
// rule): a malformed value is dropped, never thrown on, a default is an absent
// parameter, and a value the route refuses 400 is dropped before any request
// exists. The deal list's money order meets D-301 here exactly as the quote
// book's `total_minor` does: the router calls this with the search alone, so
// the verdict is `unknown` and the token survives; the screen validates again
// with the reader's verdict, and only `refused` drops it.

import type { components } from "@assessly/api-client";
import { peekSearchParam } from "../../../../components/peek-address";
import { viewIdParam } from "../../views/lib/screens";
import {
  monthsBetween,
  oneOf,
  salesMonthParam,
  searchTextParam,
  trueOnlyParam,
  uuidParam,
  type FilterKeyPin,
} from "../lib/search-grammar";
import { isValueOrderToken, type ValueSortVerdict } from "../lib/value-order";

// ---------------------------------------------------------------------------
// /sales/opportunities, THE DEAL LIST
// ---------------------------------------------------------------------------

/** The order tokens of `GET /v1/opportunities` (`-` prefix = descending). */
export type OpportunitiesOrderToken = components["schemas"]["OpportunitiesOrder"];

/** The declared default (spec 09 §4.14): close month, ties by `created_at`
 * and then `id`, the board's order. */
export const OPPORTUNITIES_DEFAULT_ORDER: OpportunitiesOrderToken = "expected_close_month";

// `Record<OpportunitiesOrderToken, true>` is the both-ways pin: a contract
// token missing here fails tsc, and a token lingering after it left the
// contract fails the moment the client regenerates.
const OPPORTUNITIES_ORDER_SET: Record<OpportunitiesOrderToken, true> = {
  expected_close_month: true,
  "-expected_close_month": true,
  expected_value_minor: true,
  "-expected_value_minor": true,
  stage_entered_at: true,
  "-stage_entered_at": true,
  "-updated_at": true,
};

/** Every order token the list route takes, in the contract's order. */
export const OPPORTUNITIES_ORDER_TOKENS = Object.keys(
  OPPORTUNITIES_ORDER_SET,
) as readonly OpportunitiesOrderToken[];

export function isOpportunitiesOrderToken(value: unknown): value is OpportunitiesOrderToken {
  return typeof value === "string" && Object.hasOwn(OPPORTUNITIES_ORDER_SET, value);
}

/** The deal's money key. The route refuses `order=±expected_value_minor` to a
 * reader without `sales.read_value` (§7 narrowing 12), as the books refuse
 * `±total_minor`. */
export const DEAL_VALUE_ORDER_KEY = "expected_value_minor";

/** Is this token a money order on the deal list? */
export function isDealValueOrderToken(token: string): boolean {
  const key = token.startsWith("-") ? token.slice(1) : token;
  return key === DEAL_VALUE_ORDER_KEY || isValueOrderToken(token);
}

type OpportunitiesViewFilters = components["schemas"]["SavedViewOpportunitiesConfig"]["filters"];

/** The statuses the address and a view hold. An absent status is `open`. */
export const OPPORTUNITY_CLOSED_STATUSES = ["won", "lost"] as const satisfies readonly NonNullable<
  OpportunitiesViewFilters["status"]
>[];

/** §2.12's four reason codes, which a lost deal shares with a rejected quote. */
export const OPPORTUNITY_LOST_REASONS = [
  "price",
  "lead_time",
  "stock",
  "other",
] as const satisfies readonly NonNullable<OpportunitiesViewFilters["lost_reason"]>[];

export interface SalesOpportunitiesSearch {
  /** The saved view the address names (spec 16 §3.12 item 5). */
  readonly view?: string | undefined;
  /** `won` or `lost`; absent is open. Sent as `status`. */
  readonly status?: NonNullable<OpportunitiesViewFilters["status"]> | undefined;
  /** A stage id, sent as `stage_id`. */
  readonly stage?: string | undefined;
  /** The owning user's id, sent as `owner_user_id`. */
  readonly owner?: string | undefined;
  /** The customer's id, sent as `customer_id`. */
  readonly customer?: string | undefined;
  /** `YYYY-MM`, sent under the same name. */
  readonly close_from?: string | undefined;
  readonly close_to?: string | undefined;
  readonly rotting?: true | undefined;
  readonly needs_next_step?: true | undefined;
  readonly lost_reason?: NonNullable<OpportunitiesViewFilters["lost_reason"]> | undefined;
  /** The search over the title, the number and the customer's name. */
  readonly q?: string | undefined;
  readonly order?: OpportunitiesOrderToken | undefined;
  /** The side peek, `<kind>:<uuid>`; never a filter. */
  readonly peek?: string | undefined;
}

// The screen's URL filter keys, pinned to its view config.
const OPPORTUNITIES_FILTER_KEYS: FilterKeyPin<SalesOpportunitiesSearch, OpportunitiesViewFilters> =
  {
    status: true,
    stage: true,
    owner: true,
    customer: true,
    close_from: true,
    close_to: true,
    rotting: true,
    needs_next_step: true,
    lost_reason: true,
    q: true,
  };
export const SALES_OPPORTUNITIES_FILTER_KEYS = Object.keys(
  OPPORTUNITIES_FILTER_KEYS,
) as readonly (keyof OpportunitiesViewFilters)[];

export function validateSalesOpportunitiesSearch(
  search: Record<string, unknown>,
  valueSort: ValueSortVerdict = "unknown",
): SalesOpportunitiesSearch {
  const order = search["order"];
  return {
    view: viewIdParam(search["view"]),
    status: oneOf(OPPORTUNITY_CLOSED_STATUSES, search["status"]),
    stage: uuidParam(search["stage"]),
    owner: uuidParam(search["owner"]),
    customer: uuidParam(search["customer"]),
    close_from: salesMonthParam(search["close_from"]),
    close_to: salesMonthParam(search["close_to"]),
    rotting: trueOnlyParam(search["rotting"]),
    needs_next_step: trueOnlyParam(search["needs_next_step"]),
    lost_reason: oneOf(OPPORTUNITY_LOST_REASONS, search["lost_reason"]),
    q: searchTextParam(search["q"]),
    order:
      isOpportunitiesOrderToken(order) && !(valueSort === "refused" && isDealValueOrderToken(order))
        ? order
        : undefined,
    peek: peekSearchParam(search["peek"]),
  };
}

// ---------------------------------------------------------------------------
// /sales/pipeline, THE BOARD
// ---------------------------------------------------------------------------

/** The filters the board and the forecast share with the list, under the
 * list's own keys, so the tab strip can carry them across. The `satisfies`
 * holds each one to a key of all three addresses. */
export const PIPELINE_SHARED_FILTER_KEYS = [
  "owner",
  "customer",
] as const satisfies readonly (keyof SalesOpportunitiesSearch &
  keyof SalesPipelineSearch &
  keyof SalesForecastSearch)[];

export interface SalesPipelineSearch {
  /** The owning user's id, sent as `owner_user_id` (admins only). */
  readonly owner?: string | undefined;
  /** The customer picker's choice, sent as `customer_id`. */
  readonly customer?: string | undefined;
  /** Opens New deal with this customer locked (the inbox's Create deal door). */
  readonly new_deal?: string | undefined;
  /** The conversation the deal comes from, read only beside `new_deal`. */
  readonly from_conversation?: string | undefined;
  /** The side peek, `<kind>:<uuid>`. */
  readonly peek?: string | undefined;
}

export function validateSalesPipelineSearch(search: Record<string, unknown>): SalesPipelineSearch {
  const newDeal = uuidParam(search["new_deal"]);
  return {
    owner: uuidParam(search["owner"]),
    customer: uuidParam(search["customer"]),
    new_deal: newDeal,
    from_conversation: newDeal === undefined ? undefined : uuidParam(search["from_conversation"]),
    peek: peekSearchParam(search["peek"]),
  };
}

// ---------------------------------------------------------------------------
// /sales/forecast, THE FORECAST
// ---------------------------------------------------------------------------

/** `to_month` is at most this many months after `from_month` (spec 09 §5,
 * `OPPORTUNITY_FORECAST_MAX_SPAN_MONTHS` in the contract). */
export const FORECAST_MAX_SPAN_MONTHS = 24;

export interface SalesForecastSearch {
  /** The first month, `YYYY-MM`; the route defaults it to the tenant's
   * current month. */
  readonly from_month?: string | undefined;
  /** The last month, `YYYY-MM`; the route defaults it to five months after
   * the first. */
  readonly to_month?: string | undefined;
  readonly owner?: string | undefined;
  readonly customer?: string | undefined;
}

/**
 * The forecast's address. A pair the route refuses 400 (an end before the
 * start, or more than 24 months after it) loses its END, and the route then
 * answers the start with its default end, five months on, which is always in
 * range. A lone `to_month` is kept: whether it runs forward depends on the
 * tenant's current month, which only the server knows, and the screen shows
 * the route's refusal as it shows any failed read.
 */
export function validateSalesForecastSearch(search: Record<string, unknown>): SalesForecastSearch {
  const from = salesMonthParam(search["from_month"]);
  const to = salesMonthParam(search["to_month"]);
  const span = from !== undefined && to !== undefined ? monthsBetween(from, to) : undefined;
  return {
    from_month: from,
    to_month:
      span === undefined || (span >= 0 && span <= FORECAST_MAX_SPAN_MONTHS) ? to : undefined,
    owner: uuidParam(search["owner"]),
    customer: uuidParam(search["customer"]),
  };
}

/**
 * The deal-list address a forecast cell links to (spec 09 §4.14: "each cell
 * linking to the list filtered to its month"): the list's open deals closing
 * in `month`, under the forecast's own owner and customer filters, so the list
 * adds up to the Deals, Pipeline or Weighted cell it came from. The list has
 * no value filter (spec 16 §9.23), so a Without a value cell opens the same
 * month, where its deals are the rows that show no value. Members the
 * forecast does not hold are left out, so the address carries no empty
 * parameter.
 */
export function forecastMonthListSearch(
  month: string,
  filters: Pick<SalesForecastSearch, "owner" | "customer">,
): SalesOpportunitiesSearch {
  return {
    close_from: month,
    close_to: month,
    ...(filters.owner !== undefined ? { owner: filters.owner } : {}),
    ...(filters.customer !== undefined ? { customer: filters.customer } : {}),
  };
}
